import React from "react";
import { flexRender, Row } from "@tanstack/react-table";
import { styled } from "@mui/material";
import { css } from "@emotion/react";

type Props<T> = {
  row: Row<T>;
  rowHeight?: number;
  isDense?: boolean;
};

const DataTableRow: React.FC<Props<any>> = ({ row, rowHeight, isDense = false, ...otherProps }) => {
  return (
    <StyledTableRow
      $isSelected={row.getIsSelected()}
      $canSelect={false}
      $height={rowHeight}
      onClick={row.getToggleSelectedHandler()}
      {...otherProps}
      tabIndex={row.getCanSelect() ? 0 : -1}
    >
      {row.getVisibleCells().map((cell) => (
        <StyledTd
          key={cell.id}
          $isDense={isDense}
          {...(typeof cell.getValue() === "string" || typeof cell.getValue() === "number"
            ? { title: cell.getValue() }
            : {})}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </StyledTd>
      ))}
    </StyledTableRow>
  );
};

const StyledTableRow = styled("tr", {
  shouldForwardProp: (prop) => !["$canSelect", "$isSelected", "$height"].includes(prop.toString()),
})<{
  $isSelected: boolean;
  $canSelect: boolean;
  $height: number;
}>(
  ({ theme, $isSelected, $canSelect, $height }) => css`
    ${$isSelected &&
    `
      background-color: ${theme.palette.background.info};
      box-shadow: inset 4px 0 0 ${theme.palette.primary.main};
    `}

    &:hover {
      background-color: ${theme.palette.grey[400]};
    }

    ${$canSelect &&
    `
      &:focus-visible {
        border: 2px solid ${theme.palette.primary.main};
      }
    `}

    ${$height && `height: ${$height}px;`}

    &:first-child > td {
      border-top: 1px solid ${theme.palette.grey[300]};
    }
  `
);

const StyledTd = styled("td", { shouldForwardProp: (prop) => prop !== "$isDense" && prop !== "$isCenterAligned" })<{
  $isDense: boolean;
  $isCenterAligned: boolean;
}>(
  ({ theme, $isDense, $isCenterAligned }) => css`
    border-bottom: 1px solid ${theme.palette.grey[300]};
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 400;
    color: ${theme.palette.text.secondary};
    border-right: 1px solid ${theme.palette.grey[300]};

    ${$isDense && `padding: 0 ${theme.spacing(1)};`}

    ${$isCenterAligned && `text-align: center;`}
  `
);

export default DataTableRow;
